/**
 * m3 echo Frontend - Autopilot Log Tab (v4.9.0)
 *
 * Displays completed autopilot runs aggregated by time across all tenants.
 * Shows: timestamp, project count, prompt count, model health status.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { autopilotLogApi, AutopilotLogEntry } from '../../services/admin';

/**
 * Format timestamp for log entry (converts UTC to local time)
 */
const formatTimestamp = (dateStr: string | null): string => {
  if (!dateStr) return '';
  // Ensure the date is parsed as UTC if no timezone is specified
  let date: Date;
  if (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10)) {
    // Already has timezone info
    date = new Date(dateStr);
  } else {
    // No timezone - assume UTC and append Z
    date = new Date(dateStr + 'Z');
  }
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

/**
 * Format an autopilot log entry as a log line
 */
const formatLogEntry = (entry: AutopilotLogEntry): string => {
  const timestamp = formatTimestamp(entry.triggered_at);
  const projectText = entry.project_count === 1 ? 'project' : 'projects';
  const promptText = entry.prompt_count === 1 ? 'prompt' : 'prompts';
  const healthyText = entry.models_healthy === 1 ? 'model healthy' : 'models healthy';

  let logLine = `${timestamp} - Autopilot completed, ${entry.project_count} ${projectText}, ${entry.prompt_count} ${promptText}, ${entry.models_healthy} ${healthyText}`;

  if (entry.models_unhealthy > 0) {
    const unhealthyText = entry.models_unhealthy === 1 ? 'model unhealthy' : 'models unhealthy';
    const modelNames = entry.unhealthy_model_names.length > 0
      ? ` (${entry.unhealthy_model_names.join(', ')})`
      : '';
    logLine += `, ${entry.models_unhealthy} ${unhealthyText}${modelNames}`;
  }

  return logLine;
};

/**
 * Get color for a log entry based on status
 * - Black for successful completions (all models healthy or no stats data)
 * - Red for failures or unhealthy models
 */
const getEntryColor = (entry: AutopilotLogEntry): string => {
  // Red only if there are actual unhealthy models (> 0) or explicit failure status
  if (entry.models_unhealthy > 0 || entry.status === 'failed' || entry.status === 'partial') {
    return '#c62828'; // Red
  }
  // Black for successful completions (including entries without stats data)
  return '#000000';
};

export const AutopilotTab: React.FC = () => {
  const [entries, setEntries] = useState<AutopilotLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logLimit, setLogLimit] = useState<number>(100);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<number>(5000);

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      loadLogs();
      const interval = setInterval(loadLogs, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, logLimit]);

  const loadLogs = async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const response = await autopilotLogApi.getAutopilotLogs(logLimit);
      setEntries(response.data.entries || []);
    } catch (error: any) {
      console.error('Error loading autopilot logs:', error);
      setLogsError(error.response?.data?.detail || 'Error loading autopilot logs');
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Autopilot Log</Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadLogs}
          disabled={logsLoading}
        >
          Aktualisieren
        </Button>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 3, alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography gutterBottom>
            Anzahl Einträge: {logLimit}
          </Typography>
          <Slider
            value={logLimit}
            onChange={(_, newValue) => setLogLimit(newValue as number)}
            min={10}
            max={1000}
            step={10}
            marks={[
              { value: 10, label: '10' },
              { value: 100, label: '100' },
              { value: 500, label: '500' },
              { value: 1000, label: '1000' },
            ]}
          />
        </Box>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Auto-Refresh</InputLabel>
          <Select
            value={autoRefresh ? refreshInterval : 0}
            label="Auto-Refresh"
            onChange={(e) => {
              const value = e.target.value as number;
              if (value === 0) {
                setAutoRefresh(false);
              } else {
                setAutoRefresh(true);
                setRefreshInterval(value);
              }
            }}
          >
            <MenuItem value={0}>Aus</MenuItem>
            <MenuItem value={3000}>3 Sekunden</MenuItem>
            <MenuItem value={5000}>5 Sekunden</MenuItem>
            <MenuItem value={10000}>10 Sekunden</MenuItem>
            <MenuItem value={30000}>30 Sekunden</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {logsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {logsError}
        </Alert>
      )}

      {logsLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {!logsLoading && entries.length > 0 && (
        <Paper
          sx={{
            p: 2,
            bgcolor: '#ffffff',
            fontFamily: 'monospace',
            fontSize: '13px',
            maxHeight: '600px',
            overflow: 'auto',
            border: '1px solid #e0e0e0',
          }}
        >
          {entries.map((entry, index) => (
            <Box
              key={`${entry.triggered_at}-${index}`}
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                borderBottom: index < entries.length - 1 ? '1px solid #eee' : 'none',
                py: 0.75,
                color: getEntryColor(entry),
              }}
            >
              {formatLogEntry(entry)}
            </Box>
          ))}
        </Paper>
      )}

      {!logsLoading && entries.length === 0 && (
        <Alert severity="info">
          Keine Autopilot-Läufe vorhanden
        </Alert>
      )}
    </Box>
  );
};

export default AutopilotTab;
